import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import styles from './styles.module.scss';
import MetricChartTooltip from './MetricChartTooltip.jsx';
import { convertSecondsToHours, convertSecondsToHoursAndMinutes } from '../../utils/dates';
import { MetricChartDotRadius, MetricChartXAxisHeight, MetricChartYAxisWidth } from './constants/index.jsx';
import { MetricChartHeader } from './index.jsx';
import { BreakpointMobile } from '../../constants/ui';
import { ChartColorsMap } from '../Charts/constants/index.jsx';

const ChartTooltip = ({ active, payload, sensor }) => {
  if (!active || !payload || payload.length < 1) return null;
  const value = convertSecondsToHoursAndMinutes(payload[0].value);
  const valueCompared =
    payload.length > 1
      ? [
          { name: sensor.name, value: `${value}`, color: ChartColorsMap.dew },
          {
            name: sensor.name2,
            value: `${convertSecondsToHoursAndMinutes(payload[1].value)}`,
            color: ChartColorsMap.dewCompared,
          },
        ]
      : null;
  return value !== null && <MetricChartTooltip value={value} comparedData={valueCompared} />;
};

const MetricChartDewDuration = ({ title, data, sensor, widthViewport }) => {
  const { selectedSecondarySensor } = useSelector((state) => state.sensors);
  const [domainYAsis, setDomainYAsis] = useState(null);
  const [ticksYAsis, setTicksYAsis] = useState(null);

  useEffect(() => {
    const domain = data.domain;
    if (domain && domain.length > 1) {
      setDomainYAsis(domain);
      const ticks =
        domain[0] === domain[1] ? [domain[0]] : [domain[0], Math.round((domain[0] + domain[1]) / 2), domain[1]];
      setTicksYAsis(ticks);
    }
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <div className={styles.metricChart}>
      {widthViewport > BreakpointMobile && (
        <MetricChartHeader
          title={title}
          sensorName={sensor.name}
          sensorNameCompared={sensor.name2}
          color={ChartColorsMap.dew}
          colorCompared={ChartColorsMap.dewCompared}
        />
      )}

      <ResponsiveContainer width="100%" height={140} className="chart-report">
        <AreaChart
          width={'100%'}
          height={140}
          data={data.data}
          margin={{
            top: 8,
            right: 8,
            left: 0,
            bottom: 0,
          }}
        >
          <CartesianGrid strokeDasharray="0 0" stroke={'var(--neutral-8)'} vertical={false} />
          <XAxis dataKey="date" tickLine={false} height={MetricChartXAxisHeight} />
          <YAxis
            ticks={ticksYAsis}
            tickCount={3}
            tickSize={0}
            tickMargin={8}
            width={MetricChartYAxisWidth}
            domain={domainYAsis}
            tickFormatter={(value) => (value || value === 0 ? convertSecondsToHours(value) : null)}
          />
          <Tooltip content={<ChartTooltip sensor={sensor} />} />
          <Area
            dataKey="value"
            type="monotone"
            strokeWidth={2}
            stroke={ChartColorsMap.dew}
            fill="url(#gradientDew)"
            fillOpacity={0.2}
            activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.dew }}
          />
          {selectedSecondarySensor && data.data[0].value2 !== null && (
            <Area
              dataKey="value2"
              type="monotone"
              strokeWidth={2}
              stroke={ChartColorsMap.dewCompared}
              strokeDasharray="2 2"
              fill="url(#gradientDewCompared)"
              fillOpacity={0.2}
              activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.dewCompared }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
      <div className={styles.metricChartUnit}>H</div>
    </div>
  );
};

export default MetricChartDewDuration;
